import { initialState } from '@/Helpers.js';
import store from '@/store/Index.js';

/** Mirrors `EdverumFeature::GRADEBOOK_REDESIGN` (app/Enums/EdverumFeature.php). */
export const GRADEBOOK_REDESIGN_FEATURE = 'GRADEBOOK_REDESIGN';

/**
 * Whether the gradebook redesign is enabled for the current user.
 *
 * Reads the store module directly rather than going through the `$hasFeature` global
 * property: router guards run outside a component instance and have no `this`.
 *
 * The feature CLASS NAME comes from `initialState('Features')`, not
 * `store.state.features`, following `middleware/FeatureMiddleware.js`. That distinction
 * matters specifically because this runs in a route guard: `store.state.features` is
 * initialised null and committed later by `App.vue`, so on a cold load a guard can
 * observe it unset and wrongly conclude the flag is off — sending a user who does have
 * the redesign back to legacy. `window.__INITIAL_STATE__` is server-rendered and is
 * already present when the first guard runs.
 *
 * `hasActiveFeature` is called behind a typeof guard because the Auth module can commit
 * a raw payload rather than a User model, in which case the method is absent; the
 * fallback reads `user.features` directly, which is all the method does.
 *
 * Returns false whenever the answer is not knowable, so legacy stays the default in
 * every uncertain state.
 *
 * @returns {boolean}
 */
export function hasGradebookRedesignFeature() {
    const user = store.state?.Auth?.user;

    if (!user) {
        return false;
    }

    const featureClass = initialState('Features', {})?.[GRADEBOOK_REDESIGN_FEATURE];

    if (!featureClass) {
        return false;
    }

    if (typeof user.hasActiveFeature === 'function') {
        return Boolean(user.hasActiveFeature(featureClass));
    }

    return Boolean(user.features?.includes(featureClass));
}

export default hasGradebookRedesignFeature;
